import React from 'react';
import { Box, Container, Typography, Card, CardContent, CardMedia, Avatar, IconButton, Chip, TextField, InputAdornment } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CommentIcon from '@mui/icons-material/Comment';
import ShareIcon from '@mui/icons-material/Share';
import SearchIcon from '@mui/icons-material/Search';

const Feed: React.FC = () => {
  const [searchQuery, setSearchQuery] = React.useState('');

  // Mock feed data
  const feedItems = [
    {
      id: 1,
      user: { name: 'Sarah Chen', avatar: '🌸', username: 'sarahgreen' },
      action: 'harvested',
      target: '15 tomatoes',
      garden: 'Rooftop Paradise',
      time: '2 hours ago',
      image: 'https://images.unsplash.com/photo-1592841200221-a6898f307baa?w=400',
      likes: 23,
      comments: 5,
      liked: false
    },
    {
      id: 2,
      user: { name: 'Mike Johnson', avatar: '🌿', username: 'plantdad' },
      action: 'completed quest',
      target: 'Master Waterer',
      time: '3 hours ago',
      xpGained: 50,
      likes: 45,
      comments: 12,
      liked: true
    },
    {
      id: 3,
      user: { name: 'Emma Wilson', avatar: '🌻', username: 'sunflowergirl' },
      action: 'planted',
      target: '6 sunflowers',
      garden: 'Backyard Bliss',
      time: '5 hours ago',
      image: 'https://images.unsplash.com/photo-1566765854606-372e7bdee473?w=400',
      likes: 67,
      comments: 8,
      liked: false
    },
    {
      id: 4,
      user: { name: 'David Park', avatar: '🥕', username: 'veggieking' },
      action: 'reached',
      target: 'Gold League',
      time: '1 day ago',
      achievement: true,
      likes: 89,
      comments: 21,
      liked: true
    }
  ];

  const FeedCard = ({ item }: { item: any }) => {
    const [liked, setLiked] = React.useState(item.liked);
    const [likes, setLikes] = React.useState(item.likes);

    const handleLike = () => {
      setLiked(!liked);
      setLikes(liked ? likes - 1 : likes + 1);
    };

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {/* User Header */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: 'primary.light' }}>
              <Typography>{item.user.avatar}</Typography>
            </Avatar>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                {item.user.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                @{item.user.username} • {item.time}
              </Typography>
            </Box>
            {item.achievement && (
              <Chip label="Achievement" color="primary" size="small" icon={<Typography>🏆</Typography>} />
            )}
            {item.xpGained && (
              <Chip label={`+${item.xpGained} XP`} color="success" size="small" />
            )}
          </Box>

          {/* Content */}
          <Typography variant="body1" mb={2}>
            {item.action} <strong>{item.target}</strong>
            {item.garden && ` in ${item.garden}`}
          </Typography>

          {/* Image */}
          {item.image && (
            <CardMedia
              component="img"
              height="200"
              image={item.image}
              alt={item.target}
              sx={{ borderRadius: 2, mb: 2 }}
            />
          )}

          {/* Actions */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" gap={2}>
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={handleLike}>
                  {liked ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
                </IconButton>
                <Typography variant="body2" color="text.secondary">{likes}</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <IconButton size="small">
                  <CommentIcon />
                </IconButton>
                <Typography variant="body2" color="text.secondary">{item.comments}</Typography>
              </Box>
            </Box>
            <IconButton size="small">
              <ShareIcon />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 10 }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 100 }}>
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            🌱 Community Feed
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search friends and gardens..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1 }}
          />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        {/* Activity Summary */}
        <Card sx={{ mb: 2, bgcolor: 'primary.light' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Today's Community Activity
            </Typography>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Box>
                <Typography variant="h4">152</Typography>
                <Typography variant="body2">Plants Added</Typography>
              </Box>
              <Box>
                <Typography variant="h4">89</Typography>
                <Typography variant="body2">Harvests</Typography>
              </Box>
              <Box>
                <Typography variant="h4">234</Typography>
                <Typography variant="body2">Quests Completed</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Feed Items */}
        {feedItems.map(item => (
          <FeedCard key={item.id} item={item} />
        ))}
      </Container>
    </Box>
  );
};

export default Feed;